import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { data, portal, type ClientOrg, type Item, type Page } from "@aawaz/api-client";
import {
  Badge, Card, ChannelLogo, EmptyState, Hero, Icon, Notice, Ring, SkeletonRows, Stat, Steps, fmtPeriod, humanize, sourceLabel, type Step,
} from "@aawaz/ui";
import { useSession } from "../session";
import { displayName, FileIcon } from "./Documents";

interface Dash {
  period: string; totals: Record<string, number>; needs_attention: number; join_requests?: number;
  connections: { id: string; connector_key: string; display_name: string; status: string; health: string }[];
  tally_agents_online: number; mirror_targets: { id: string; status: string }[];
}
interface DeskRow { client_org_id: string; client: string; score: number; itc: { at_risk: string; unclaimed: string; has_2b: boolean }; open: unknown[] }
interface BooksSource { binding_id: string; provider: string; company_name: string | null; last_synced_at: string | null; last_error: string | null }
interface BooksActivity { posted_30d: number; held_30d: number }
interface Automation { ack: { enabled: boolean }; chase: { enabled: boolean }; autopost: { enabled: boolean } }

export const inr = (v: string | number | null | undefined) => v == null ? "—"
  : "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: Number(v) % 1 ? 2 : 0 });

export function lastMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

/** Home: what needs a person today, how this month is going, and what is left to set up. */
export function HomePage() {
  const s = useSession();
  const ca = s.has("ca");
  const books = s.has("accounting") || ca;
  const period = lastMonth();
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: () => data<Dash>(portal.GET("/portal/v1/dashboard")) });
  const review = useQuery({ queryKey: ["items", "review", "home"], queryFn: () =>
    data<Page<Item>>(portal.GET("/portal/v1/review-queue", { params: { query: { limit: 6 } } })) });
  const clients = useQuery({ enabled: ca, queryKey: ["clients"], queryFn: () => data<{ data: ClientOrg[] }>(portal.GET("/portal/v1/clients")) });
  const desk = useQuery({ enabled: ca, queryKey: ["ca-desk", period], queryFn: () =>
    data<{ data: DeskRow[] }>(portal.GET("/portal/v1/ca/desk", { params: { query: { period } } })) });
  const sources = useQuery({ enabled: books, queryKey: ["ca-books-sources"], queryFn: () =>
    data<{ data: BooksSource[] }>(portal.GET("/portal/v1/ca/books/sources")) });
  const activity = useQuery({ enabled: books, queryKey: ["ca-books-activity"], queryFn: () =>
    data<BooksActivity>(portal.GET("/portal/v1/ca/books/activity")) });
  const auto = useQuery({ queryKey: ["ca-settings"], queryFn: () => data<Automation>(portal.GET("/portal/v1/ca/settings")) });
  const staff = useQuery({ enabled: ca, queryKey: ["ca-staff"], queryFn: () => data<{ data: unknown[] }>(portal.GET("/portal/v1/ca/staff")) });
  const links = useQuery({ enabled: ca, queryKey: ["bindings", "senders"], queryFn: () =>
    data<{ data: { binding_type: string; client_org_id: string | null }[] }>(portal.GET("/portal/v1/bindings")) });

  const d = dash.data;
  const received = d ? Object.values(d.totals).reduce((a, b) => a + b, 0) : 0;
  const filed = d?.totals.ACCEPTED ?? 0;
  const rows = desk.data?.data ?? [];
  const atRisk = rows.reduce((n, r) => n + Number(r.itc.at_risk || 0), 0);
  const ready = rows.filter((r) => r.open.length === 0).length;
  // a setup that was started and never finished is not a connection
  const keys = new Set((d?.connections ?? []).filter((c) => c.status === "active" || c.status === "degraded").map((c) => c.connector_key));
  const clientLinked = (links.data?.data ?? []).some((b) => b.client_org_id && b.binding_type.startsWith("whatsapp_"));
  const steps: Step[] = [
    { key: "wa", title: "Connect WhatsApp", hint: "One click. Your firm gets the AawazAI number that clients and staff send bills to.",
      done: keys.has("whatsapp"), action: <Link className="ui-btn ui-btn-sm" to="/channels/whatsapp">Connect</Link> },
    ...(ca ? [
      { key: "clients", title: "Add your clients", hint: "With their GSTINs, so bills find the right client by themselves.",
        done: (clients.data?.data.length ?? 0) > 0, action: <Link className="ui-btn ui-btn-sm" to="/clients">Add clients</Link> },
      { key: "senders", title: "Add each client's WhatsApp number", hint: "Only numbers you have added (and your staff numbers) reach your firm. Then send clients the ready message.",
        done: clientLinked, action: <Link className="ui-btn ui-btn-sm" to="/channels/whatsapp">Add numbers</Link> },
      { key: "staff", title: "Register staff WhatsApp numbers", hint: "Staff forward bills for any client; AawazAI works out whose.",
        done: (staff.data?.data.length ?? 0) > 0, action: <Link className="ui-btn ui-btn-sm" to="/channels/whatsapp">Add staff</Link> },
    ] : []),
    { key: "drive", title: "File documents to Google Drive or OneDrive", hint: "Named and foldered by client, year, month and type.",
      // a connected drive is only read from until filing has a folder to go to
      done: (d?.mirror_targets ?? []).some((m) => m.status === "active"),
      action: <Link className="ui-btn ui-btn-sm" to={keys.has("gdrive") || keys.has("onedrive") ? "/channels/filing" : "/channels"}>{keys.has("gdrive") || keys.has("onedrive") ? "Start filing" : "Connect"}</Link> },
    { key: "books", title: "Connect the books (Zoho Books or Tally)", hint: "Synced every 15 minutes; approved bills post back.",
      done: keys.has("zoho_books") || keys.has("tally"), action: <Link className="ui-btn ui-btn-sm" to="/channels">Connect</Link> },
    { key: "auto", title: "Choose what runs on its own", hint: "Client acknowledgements, document reminders, auto-posting.",
      done: !!auto.data && (auto.data.chase.enabled || auto.data.autopost.enabled), action: s.can("tenant_settings") ? <Link className="ui-btn ui-btn-sm" to="/settings/automation">Review</Link>
        : <span className="ui-hint">Owner only</span> },
  ];
  const done = steps.filter((x) => x.done).length;

  return (
    <>
      <Hero eyebrow={fmtPeriod(d?.period)} title={`${greeting()}${s.me?.name ? `, ${s.me.name.split(" ")[0]}` : ""}`}
            actions={<Link className="ui-btn ui-btn-primary" to="/inbox"><Icon name="inbox" size={16} />Open inbox{d?.needs_attention ? ` · ${d.needs_attention}` : ""}</Link>}>
        {d ? `${received} document${received === 1 ? "" : "s"} arrived this month; ${filed} filed automatically` +
          (d.needs_attention ? `, ${d.needs_attention} waiting for you.` : ". Nothing is waiting for you.") : "Loading your month…"}
      </Hero>
      {d?.join_requests ? (
        <Notice>{d.join_requests} {d.join_requests === 1 ? "person is" : "people are"} waiting to be let into a client's WhatsApp group.{" "}
          <Link to="/channels/whatsapp">Review join requests</Link></Notice>
      ) : null}

      <div className="ui-stats">
        <Stat label="Received this month" value={received} icon="inbox" tone="brand" note="WhatsApp, email, Drive and uploads" />
        <Stat label="Filed automatically" value={received ? `${Math.round((filed / received) * 100)}%` : "—"} icon="sparkles" tone="green"
              note={`${filed} of ${received}`} />
        <Stat label="Needs you" value={<Link to="/inbox">{d?.needs_attention ?? 0}</Link>} icon="alert"
              tone={d?.needs_attention ? "amber ui-stat-alert" : undefined} note={d?.needs_attention ? "In the inbox" : "All clear"} />
        {ca ? <Stat label={`ITC at risk · ${fmtPeriod(period)}`} value={inr(atRisk)} icon="rupee" tone={atRisk ? "red" : undefined}
                    note={`${ready} of ${rows.length} returns ready`} />
          : <Stat label="Posted to the books" value={activity.data?.posted_30d ?? 0} icon="book" tone="brand"
                  note={`${activity.data?.held_30d ?? 0} held for review · 30 days`} />}
      </div>

      <div className="ui-split">
        <div className="ui-stack">
          <Card title="Needs you" actions={<Link to="/inbox">Open inbox</Link>}>
            {review.isLoading ? <SkeletonRows rows={4} /> : (review.data?.data.length ?? 0) === 0 ? (
              <EmptyState icon="checkCircle" title="You're all caught up">Nothing is waiting for a decision.</EmptyState>
            ) : (
              <div className="ui-list">
                {review.data!.data.map((it) => (
                  <Link key={it.id} to={`/inbox?open=${it.id}`} className="ui-list-item" style={{ color: "inherit", textDecoration: "none" }}>
                    <FileIcon item={it} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span className="ui-cell-title">{displayName(it)}</span>
                      <span className="ui-cell-sub">{sourceLabel(it.source)}{it.sender?.name ? ` · ${it.sender.name}` : ""}</span>
                    </div>
                    {it.reason_code ? <Badge value={it.reason_code} tone="amber" /> : <Badge value={it.status} />}
                  </Link>
                ))}
              </div>
            )}
          </Card>
          {ca ? (
            <Card title={`GST · ${fmtPeriod(period)}`} actions={<Link to="/gst">Open GST desk</Link>}>
              {desk.isLoading ? <SkeletonRows rows={4} /> : rows.length === 0 ? (
                <EmptyState icon="building" title="No clients yet" action={<Link className="ui-btn ui-btn-primary" to="/clients">Add clients</Link>}>
                  Add clients to see how ready each return is.
                </EmptyState>
              ) : (
                <div className="ui-list">
                  {rows.slice(0, 6).map((r) => (
                    <Link key={r.client_org_id} to={`/clients/${r.client_org_id}`} className="ui-list-item" style={{ color: "inherit", textDecoration: "none" }}>
                      <Ring value={r.score} size={38} stroke={4} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <span className="ui-cell-title">{r.client}</span>
                        <span className="ui-cell-sub">{r.itc.has_2b ? `ITC at risk ${inr(r.itc.at_risk)} · unclaimed ${inr(r.itc.unclaimed)}` : "GSTR-2B not received yet"}</span>
                      </div>
                      <Icon name="chevron" size={16} />
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          ) : null}
        </div>
        <div className="ui-stack">
          {done < steps.length ? (
            <Card title={<><Ring value={(done / steps.length) * 100} size={30} stroke={4} label={`${done}`} />Getting started</>}
                  subtitle={`${done} of ${steps.length} done`}>
              <Steps steps={steps} />
            </Card>
          ) : null}
          {books ? (
            <Card title="Books" actions={<Link to="/books">Open</Link>}>
              {(sources.data?.data.length ?? 0) === 0 ? (
                <p className="ui-hint" style={{ margin: 0 }}>{keys.has("zoho_books") || keys.has("tally")
                  ? <>Your books are connected but not yet linked to a client, so nothing is synced. <Link to="/channels">Link clients</Link> on the books channel.</>
                  : "No books connected. Connect Zoho Books or Tally under Channels."}</p>
              ) : (
                <div className="ui-list">
                  {sources.data!.data.slice(0, 4).map((b) => (
                    <div key={b.binding_id} className="ui-list-item">
                      <ChannelLogo kind={b.provider} size={26} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <span className="ui-cell-title">{b.company_name ?? sourceLabel(b.provider)}</span>
                        <span className="ui-cell-sub">{b.last_error ? "Sync needs attention" : b.last_synced_at ? `Synced ${new Date(b.last_synced_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : "Waiting for first sync"}</span>
                      </div>
                      <span className={`ui-dot ui-${b.last_error ? "red" : b.last_synced_at ? "green" : "amber"}`} />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ) : null}
          <Card title="Channels" actions={<Link to="/channels">Manage</Link>}>
            {(d?.connections.length ?? 0) === 0 ? <p className="ui-hint" style={{ margin: 0 }}>Nothing connected yet.</p> : (
              <div className="ui-list">
                {d!.connections.map((c) => (
                  <div key={c.id} className="ui-list-item">
                    <ChannelLogo kind={c.connector_key} size={26} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span className="ui-cell-title">{sourceLabel(c.connector_key)}</span>
                      <span className="ui-cell-sub">{humanize(c.status)}</span>
                    </div>
                    <span className={`ui-dot ui-${c.health === "green" ? "green" : c.health === "red" ? "red" : "amber"}`} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
